import React, { useState } from 'react';
import {
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  ArrowDropDown as ArrowDropDownIcon,
  Category,
  GridView,
  Layers,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { DASHBOARD_ACCENT, resolveDashboardThemeMode } from '../../utils/dashboardSurfaceSx';
import {
  getShellMenuItemSx,
  getShellMenuPaperSx,
} from '../../utils/mobileShellMenuLayout';

const WORKSPACE_CONTROL_HEIGHT = 36;

const GROUP_VIEW_LABELS = {
  full: 'Full View',
  floor: 'Floor View',
  room_type: 'Room Type View',
};

const GROUP_VIEW_ICONS = {
  full: GridView,
  floor: Layers,
  room_type: Category,
};

const groupViewMenuFocusProps = {
  autoFocus: false,
  disableAutoFocusItem: true,
  disableRestoreFocus: true,
  MenuListProps: { autoFocusItem: false, sx: { py: 0 } },
};

const groupViewMenuPaperSx = (isDark) => ({
  ...getShellMenuPaperSx(isDark),
  width: 240,
  minWidth: 240,
  borderRadius: '12px',
  padding: '8px 0',
  marginTop: '8px',
});

const groupViewMenuItemSx = (isDark, selected) => ({
  ...getShellMenuItemSx(isDark),
  height: 40,
  px: 2,
  ...(selected
    ? {
        backgroundColor: isDark ? alpha('#4ce1b6', 0.1) : 'rgba(76, 225, 182, 0.08)',
        '&:hover': {
          backgroundColor: isDark ? alpha('#4ce1b6', 0.14) : 'rgba(76, 225, 182, 0.12)',
        },
      }
    : null),
});

export default function WalkInRoomGroupViewControl({
  groupView = 'room_type',
  onGroupViewChange,
  themeMode = 'dark',
}) {
  const isDark = resolveDashboardThemeMode(themeMode) === 'dark';
  const [anchorEl, setAnchorEl] = useState(null);

  const groupButtonSx = {
    backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
    color: isDark ? '#f8fafc' : '#0f172a',
    fontWeight: 700,
    fontSize: '0.8125rem',
    minWidth: { xs: 112, sm: 132 },
    height: WORKSPACE_CONTROL_HEIGHT,
    minHeight: WORKSPACE_CONTROL_HEIGHT,
    px: 1.25,
    textTransform: 'none',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
    borderRadius: '10px',
    flexShrink: 0,
    '& .MuiButton-endIcon': {
      ml: 0.35,
      '& > *:nth-of-type(1)': { fontSize: 20 },
    },
    '&:hover': {
      backgroundColor: isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.08)',
      borderColor: alpha(DASHBOARD_ACCENT, 0.35),
    },
  };

  return (
    <>
      <Button
        variant="outlined"
        endIcon={<ArrowDropDownIcon />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={groupButtonSx}
      >
        {GROUP_VIEW_LABELS[groupView] ?? groupView}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        {...groupViewMenuFocusProps}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: groupViewMenuPaperSx(isDark) }}
      >
        {(['full', 'floor', 'room_type']).map((key) => {
          const Icon = GROUP_VIEW_ICONS[key];
          const selected = groupView === key;
          return (
            <MenuItem
              key={key}
              selected={selected}
              onClick={() => {
                onGroupViewChange?.(key);
                setAnchorEl(null);
              }}
              sx={groupViewMenuItemSx(isDark, selected)}
            >
              <ListItemIcon>
                <Icon sx={{ fontSize: 18 }} />
              </ListItemIcon>
              <ListItemText
                primary={GROUP_VIEW_LABELS[key]}
                primaryTypographyProps={{ fontSize: '14px', fontWeight: 600 }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
